/**
 * @typedef {import('../types.js').State} State
 * @typedef {import('../types.js').SafeConfig} SafeConfig
 */ import { patternCompile } from './pattern-compile.js';
import { patternInScope } from './pattern-in-scope.js';
/**
 * Make a string safe for embedding in markdown constructs.
 *
 * In markdown, almost all punctuation characters can, in certain cases,
 * result in something.
 * Whether they do is highly subjective to where they happen and in what
 * they happen.
 *
 * To solve this, `mdast-util-to-markdown` tracks:
 *
 * * Characters before and after something;
 * * What “constructs” we are in.
 *
 * This information is then used by this function to escape or encode
 * special characters.
 *
 * @param {State} state
 *   Info passed around about the current state.
 * @param {string | null | undefined} input
 *   Raw value to make safe.
 * @param {SafeConfig} config
 *   Configuration.
 * @returns {string}
 *   Serialized markdown safe for embedding.
 */ export function safe(state, input, config) {
    var value = (config.before || '') + (input || '') + (config.after || '');
    /** @type {Array<number>} */ var positions = [];
    /** @type {Array<string>} */ var result = [];
    /** @type {Record<number, {before: boolean, after: boolean}>} */ var infos = {};
    var index = -1;
    while(++index < state.unsafe.length){
        var pattern = state.unsafe[index];
        if (!patternInScope(state.stack, pattern)) {
            continue;
        }
        var expression = patternCompile(pattern);
        /** @type {RegExpExecArray | null} */ var match = void 0;
        while(match = expression.exec(value)){
            var before = 'before' in pattern || Boolean(pattern.atBreak);
            var after = 'after' in pattern;
            var position = match.index + (before ? match[1].length : 0);
            if (positions.includes(position)) {
                if (infos[position].before && !before) {
                    infos[position].before = false;
                }
                if (infos[position].after && !after) {
                    infos[position].after = false;
                }
            } else {
                positions.push(position);
                infos[position] = {
                    before: before,
                    after: after
                };
            }
        }
    }
    positions.sort(numerical);
    var start = config.before ? config.before.length : 0;
    var end = value.length - (config.after ? config.after.length : 0);
    index = -1;
    while(++index < positions.length){
        var position1 = positions[index];
        // Character before or after matched:
        if (position1 < start || position1 >= end) {
            continue;
        }
        // If this character is supposed to be escaped because it has a condition on
        // the next character, and the next character is definitly being escaped,
        // then skip this escape.
        if (position1 + 1 < end && positions[index + 1] === position1 + 1 && infos[position1].after && !infos[position1 + 1].before && !infos[position1 + 1].after || positions[index - 1] === position1 - 1 && infos[position1].before && !infos[position1 - 1].before && !infos[position1 - 1].after) {
            continue;
        }
        if (start !== position1) {
            // If we have to use a character reference, an ampersand would be more
            // correct, but as backslashes only care about punctuation, either will
            // do the trick
            result.push(escapeBackslashes(value.slice(start, position1), '\\'));
        }
        start = position1;
        if (/[!-/:-@[-`{-~]/.test(value.charAt(position1)) && (!config.encode || !config.encode.includes(value.charAt(position1)))) {
            // Character escape.
            result.push('\\');
        } else {
            // Character reference.
            result.push('&#x' + value.charCodeAt(position1).toString(16).toUpperCase() + ';');
            start++;
        }
    }
    result.push(escapeBackslashes(value.slice(start, end), config.after));
    return result.join('');
}
/**
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */ function numerical(a, b) {
    return a - b;
}
/**
 * @param {string} value
 * @param {string} after
 * @returns {string}
 */ function escapeBackslashes(value, after) {
    var expression = /\\(?=[!-/:-@[-`{-~])/g;
    /** @type {Array<number>} */ var positions = [];
    /** @type {Array<string>} */ var results = [];
    var whole = value + after;
    var index = -1;
    var start = 0;
    /** @type {RegExpExecArray | null} */ var match;
    while(match = expression.exec(whole)){
        positions.push(match.index);
    }
    while(++index < positions.length){
        if (start !== positions[index]) {
            results.push(value.slice(start, positions[index]));
        }
        results.push('\\');
        start = positions[index];
    }
    results.push(value.slice(start));
    return results.join('');
}
